import * as authService from './authService'
import { getRuntimeTenantIdSync } from '../lib/runtimeTenant'
import { withApiBaseUrl } from '../lib/apiBaseUrl'

const BASE = '/api/v1/huay-jackpot'

export type HuayJackpotPayoutMode = 'multiplier' | 'fixed' | 'jackpot'

export interface HuayJackpotPrizeTier {
  id: string
  whiteMatch: number
  redMatch: boolean
  payoutMode: HuayJackpotPayoutMode
  multiplier?: number
  fixedAmount?: number
  label?: string
}

export interface HuayJackpotPlayConfig {
  salesOpen: boolean
  userEntryVisible: boolean
  ticketPrice: number
  paymentType: 'cash'
  displayJackpotThb: number
  jackpotCapThb: number
  prizeTiers: HuayJackpotPrizeTier[]
  rules: {
    whitePick: number
    whiteMax: number
    redMax: number
  }
}

export interface HuayJackpotTicket {
  id: string
  ticketPrice: number
  whiteNumbers: number[]
  redNumber: number
  drawnWhite?: number[]
  drawnRed?: number | null
  whiteMatch?: number
  redMatch?: boolean
  tierId?: string | null
  tierLabel?: string | null
  isJackpot?: boolean
  prizeAmount?: number
  status?: string
  purchasedAt?: string | null
  settledAt?: string | null
  createdAt?: string
}

export interface HuayJackpotPurchaseResult {
  ticket: HuayJackpotTicket
  newBalance?: number
  displayJackpotThb?: number
}

export interface HuayJackpotHistoryPage {
  items: HuayJackpotTicket[]
  total: number
  page: number
  limit: number
}

function headers(extra?: Record<string, string>) {
  const token = authService.getToken()
  const tenantId = getRuntimeTenantIdSync()
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
    ...(extra || {}),
  }
}

async function parseJson<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    const err = new Error(
      (data as { message?: string })?.message || `HTTP ${response.status}`,
    ) as Error & { code?: string; status?: number }
    err.code = (data as { code?: string })?.code
    err.status = response.status
    throw err
  }
  return data as T
}

export async function fetchHuayJackpotPlayConfig() {
  const response = await fetch(withApiBaseUrl(`${BASE}/play-config`), {
    headers: headers(),
  })
  return parseJson<{ success: boolean; data: HuayJackpotPlayConfig }>(response)
}

export async function purchaseHuayJackpotTicket(body: {
  whiteNumbers: number[]
  redNumber: number
}) {
  const response = await fetch(withApiBaseUrl(`${BASE}/purchase`), {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(body),
  })
  return parseJson<{ success: boolean; data: HuayJackpotPurchaseResult }>(response)
}

export async function fetchMyHuayJackpotHistory(params: { page?: number; limit?: number } = {}) {
  const qs = new URLSearchParams()
  if (params.page) qs.set('page', String(params.page))
  if (params.limit) qs.set('limit', String(params.limit))
  const q = qs.toString() ? `?${qs}` : ''
  const response = await fetch(withApiBaseUrl(`${BASE}/my/history${q}`), {
    headers: headers(),
  })
  return parseJson<{ success: boolean; data: HuayJackpotHistoryPage }>(response)
}
